/**
 * Authentication and authorization utility functions
 */

// Define user roles
export const USER_ROLES = {
  CUSTOMER: "customer",
  ADMIN: "admin",
  SALES: "sales",
  LEADS: "leads",
  ORDERS: "orders",
  TELECALLER: "telecaller",
};

/**
 * Check if user can make purchases (only customers can purchase)
 * @param {Object} user - User object with role property
 * @returns {boolean} - Whether user can make purchases
 */
export const canUserPurchase = (user) => {
  return user && user.role === USER_ROLES.CUSTOMER;
};

/**
 * Check if user is authenticated
 * @param {Object} user - User object
 * @param {string} token - Authentication token
 * @returns {boolean} - Whether user is authenticated
 */
export const isAuthenticated = (user, token) => {
  return !!(user && token);
};

/**
 * Check if user has specific role
 * @param {Object} user - User object with role property
 * @param {string} requiredRole - Required role to check
 * @returns {boolean} - Whether user has the required role
 */
export const hasRole = (user, requiredRole) => {
  return user && user.role === requiredRole;
};

/**
 * Get user-friendly role name
 * @param {string} role - User role
 * @returns {string} - User-friendly role name
 */
export const getRoleName = (role) => {
  const roleNames = {
    [USER_ROLES.CUSTOMER]: "Customer",
    [USER_ROLES.ADMIN]: "Administrator",
    [USER_ROLES.SALES]: "Sales Representative",
    [USER_ROLES.LEADS]: "Leads Manager",
    [USER_ROLES.ORDERS]: "Orders Manager",
    [USER_ROLES.TELECALLER]: "Telecaller",
  };

  return roleNames[role] || "Unknown Role";
};

/**
 * Create redirect URL with return path
 * @param {string} currentPath - Current path to return to after login
 * @returns {string} - Login URL with return path
 */
export const createLoginRedirectUrl = (currentPath) => {
  const returnUrl = encodeURIComponent(currentPath);
  return `/login?returnUrl=${returnUrl}`;
};

/**
 * Parse return URL from login page
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {string} - Return URL or default path
 */
export const parseReturnUrl = (searchParams) => {
  const returnUrl = searchParams.get("returnUrl");
  return returnUrl ? decodeURIComponent(returnUrl) : "/";
};
